import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImage, faLock, faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'
import type { DateSort } from '../utils/advanceRowSort.ts'

export function LedgerSortChip({
  active = true,
  value,
  onActivate,
  onChange,
  name,
  ascLabel,
  descLabel,
}: {
  active?: boolean
  value: DateSort
  onActivate?: () => void
  onChange: (next: DateSort) => void
  name: string
  ascLabel: string
  descLabel: string
}) {
  const direction = value === 'asc' ? ascLabel.toLowerCase() : descLabel.toLowerCase()
  const label = active
    ? `${name} sorted ${direction}. Toggle sort direction.`
    : `Sort by ${name}`
  return (
    <button
      type="button"
      className={`badge badge-sm h-5 min-h-0 w-5 p-0 justify-center font-normal ${active ? 'badge-primary' : 'badge-outline'}`}
      title={label}
      aria-label={label}
      onClick={() => {
        if (!active && onActivate) onActivate()
        else onChange(value === 'asc' ? 'desc' : 'asc')
      }}
    >
      <FontAwesomeIcon icon={!active ? faSort : value === 'asc' ? faSortUp : faSortDown} />
    </button>
  )
}

export function DateSortChip({
  value,
  onChange,
  active,
  onActivate,
}: {
  value: DateSort
  onChange: (next: DateSort) => void
  active?: boolean
  onActivate?: () => void
}) {
  return (
    <LedgerSortChip
      active={active}
      value={value}
      onActivate={onActivate}
      onChange={onChange}
      name="date"
      ascLabel="Earliest"
      descLabel="Latest"
    />
  )
}

export function ItemCategorySortChip({
  value,
  onChange,
  active,
  onActivate,
}: {
  value: DateSort
  onChange: (next: DateSort) => void
  active?: boolean
  onActivate?: () => void
}) {
  return (
    <LedgerSortChip
      active={active}
      value={value}
      onActivate={onActivate}
      onChange={onChange}
      name="item"
      ascLabel="A–Z"
      descLabel="Z–A"
    />
  )
}

export function AdvanceDescription({
  description,
  scheduled,
  photoCount = 0,
  onViewPhotos,
}: {
  description?: string | null
  scheduled?: boolean
  photoCount?: number
  onViewPhotos?: () => void
}) {
  return (
    <div className="flex items-center gap-2 min-w-0 max-w-xs">
      <span className="truncate">{description || '—'}</span>
      {scheduled ? <span className="badge badge-warning badge-sm shrink-0">Scheduled</span> : null}
      {photoCount > 0 && onViewPhotos ? (
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-circle shrink-0"
          aria-label={photoCount === 1 ? 'View photo' : `View photos (${photoCount})`}
          title={photoCount === 1 ? 'View photo' : `${photoCount} photos`}
          onClick={(e) => {
            e.stopPropagation()
            onViewPhotos()
          }}
        >
          <FontAwesomeIcon icon={faImage} />
        </button>
      ) : null}
    </div>
  )
}

export function LockedBalanceBadge() {
  return (
    <span className="tooltip tooltip-left" data-tip="This balance is locked">
      <span className="badge badge-ghost badge-sm h-5 min-h-0 w-5 p-0" aria-label="Locked balance">
        <FontAwesomeIcon icon={faLock} className="text-[10px]" aria-hidden />
      </span>
    </span>
  )
}
